import {
	SenderCopywritingPreference,
	SenderPhraseSet,
	UpsertSenderCopywritingPreferenceRequest,
} from "@/pages/Private/redux/senderCopywriting/senderCopywriting.schema";

/**
 * The editable shape of a sender's copywriting preference (PREC-3719 §6,
 * PREC-3686). Language codes are `""` while unset — the API takes `undefined`,
 * but a controlled select needs a string.
 */
export interface SenderLanguageValue {
	primaryLanguage: string;
	secondaryLanguages: string[];
	fallbackLanguage: string;
	phrases: Record<string, SenderPhraseSet>;
}

export const EMPTY_PHRASE_SET: SenderPhraseSet = { openers: [], closings: [], ctas: [] };

export const EMPTY_SENDER_LANGUAGE_VALUE: SenderLanguageValue = {
	primaryLanguage: "",
	secondaryLanguages: [],
	fallbackLanguage: "",
	phrases: {},
};

/**
 * Sets the primary language, dropping it from the secondaries: "primary" and
 * "one of the others" are mutually exclusive states, and a language listed as
 * both would make the resolution order (primary → secondary → fallback)
 * meaningless.
 */
export const applyPrimaryLanguage = (
	value: SenderLanguageValue,
	code: string
): SenderLanguageValue => ({
	...value,
	primaryLanguage: code,
	secondaryLanguages: value.secondaryLanguages.filter((secondary) => secondary !== code),
});

export const applyFallbackLanguage = (
	value: SenderLanguageValue,
	code: string
): SenderLanguageValue => ({ ...value, fallbackLanguage: code });

/** Adds or removes a secondary language. The primary can never be toggled in. */
export const toggleSecondaryLanguage = (
	value: SenderLanguageValue,
	code: string
): SenderLanguageValue => {
	if (code === value.primaryLanguage) {
		return value;
	}

	return {
		...value,
		secondaryLanguages: value.secondaryLanguages.includes(code)
			? value.secondaryLanguages.filter((secondary) => secondary !== code)
			: [...value.secondaryLanguages, code],
	};
};

export const applyPhrase = (
	value: SenderLanguageValue,
	language: string,
	field: keyof SenderPhraseSet,
	phrases: string[]
): SenderLanguageValue => ({
	...value,
	phrases: {
		...value.phrases,
		[language]: { ...EMPTY_PHRASE_SET, ...value.phrases[language], [field]: phrases },
	},
});

/**
 * The languages that get a phrasing tab: the primary and the fallback, deduped
 * and without the unset ones. Secondaries are deliberately excluded — asking
 * for go-to lines in every language a sender merely *can* write is noise.
 */
export const phraseTabsOf = (value: SenderLanguageValue): string[] =>
	[value.primaryLanguage, value.fallbackLanguage].filter(
		(code, index, all) => code && all.indexOf(code) === index
	);

export const phraseSetOf = (value: SenderLanguageValue, language: string): SenderPhraseSet =>
	value.phrases[language] ?? EMPTY_PHRASE_SET;

/** The stored preference as an editable value — `null` codes become `""`. */
export const senderLanguageValueOf = (
	preference?: SenderCopywritingPreference
): SenderLanguageValue => {
	if (!preference) {
		return EMPTY_SENDER_LANGUAGE_VALUE;
	}

	return {
		primaryLanguage: preference.primaryLanguage ?? "",
		secondaryLanguages: preference.secondaryLanguages,
		fallbackLanguage: preference.fallbackLanguage ?? "",
		phrases: preference.phrases,
	};
};

/** The editable value as a PUT body — an unset code is omitted, not sent as `""`. */
export const senderLanguageUpsertOf = (
	value: SenderLanguageValue
): UpsertSenderCopywritingPreferenceRequest => ({
	primaryLanguage: value.primaryLanguage || undefined,
	secondaryLanguages: value.secondaryLanguages,
	fallbackLanguage: value.fallbackLanguage || undefined,
	phrases: value.phrases,
});

/**
 * Whether the edited value differs from the stored preference — drives the
 * Cancel / Save buttons. Order matters for the secondaries: the user picked it.
 */
export const hasSenderLanguageChanges = (
	value: SenderLanguageValue,
	preference?: SenderCopywritingPreference
): boolean =>
	JSON.stringify(senderLanguageUpsertOf(value)) !==
	JSON.stringify(senderLanguageUpsertOf(senderLanguageValueOf(preference)));
